import React from 'react';
import styled, { css } from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { typography } from '../tokens/typography';
import { spacing } from '../tokens/spacing';
import { generateGradient } from '../../utils/colorUtils';

/**
 * Heading - Typography heading component
 */
export type HeadingVariant = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'display';
export type HeadingColor = 'primary' | 'secondary' | 'text' | 'muted' | 'accent';

interface HeadingProps {
  variant?: HeadingVariant;
  color?: HeadingColor;
  weight?: 'light' | 'normal' | 'medium' | 'semibold' | 'bold';
  gradient?: boolean;
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  children: React.ReactNode;
  className?: string;
  id?: string;
  style?: React.CSSProperties;
  marginBottom?: string;
  /** Maps to variant: lg->h2, md->h3, sm->h4. Use variant for full control. */
  size?: string;
}

const HeadingStyled = styled.h1<{
  variant: HeadingVariant;
  color: HeadingColor;
  weight: string;
  gradient: boolean;
}>`
  margin: 0;
  font-family: ${typography.fontFamily.display};
  font-weight: ${props => typography.fontWeight[props.weight as keyof typeof typography.fontWeight]};
  line-height: ${typography.lineHeight.tight};
  letter-spacing: ${typography.letterSpacing.tighter};

  ${props => {
    const sizes = {
      display: typography.fontSize['6xl'],
      h1: typography.fontSize['5xl'],
      h2: typography.fontSize['4xl'],
      h3: typography.fontSize['3xl'],
      h4: typography.fontSize['2xl'],
      h5: typography.fontSize.xl,
      h6: typography.fontSize.lg,
    };
    return `font-size: ${sizes[props.variant]};`;
  }}

  ${props => {
    if (props.gradient) {
      return css`
        background: ${generateGradient(
          props.theme.colors.primary,
          props.theme.colors.secondary,
          '135deg'
        )};
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      `;
    }

    switch (props.color) {
      case 'primary':
        return `color: ${props.theme.colors.primary};`;
      case 'secondary':
        return `color: ${props.theme.colors.secondary};`;
      case 'muted':
        return `color: ${props.theme.colors.textMuted};`;
      case 'accent':
        return `color: ${props.theme.colors.accent};`;
      default:
        return `color: ${props.theme.colors.text};`;
    }
  }}
`;

const sizeToVariant: Record<string, HeadingVariant> = {
  lg: 'h2',
  md: 'h3',
  sm: 'h4',
};

export const Heading: React.FC<HeadingProps> = ({
  variant: variantProp = 'h1',
  color = 'text',
  weight = 'bold',
  gradient = false,
  as,
  children,
  className,
  id,
  style,
  marginBottom,
  size,
}) => {
  const variant = size && sizeToVariant[size] ? sizeToVariant[size] : variantProp;
  const tag = as || (variant === 'display' ? 'h1' : variant);
  const combinedStyle: React.CSSProperties | undefined =
    style || marginBottom ? { ...style, ...(marginBottom ? { marginBottom } : {}) } : undefined;
  return (
    <HeadingStyled
      as={tag}
      variant={variant}
      color={color}
      weight={weight}
      gradient={gradient}
      className={className}
      id={id}
      style={combinedStyle}
    >
      {children}
    </HeadingStyled>
  );
};

/**
 * Text - Typography text component
 */
export type TextSize = 'xs' | 'sm' | 'base' | 'lg' | 'xl';
export type TextColor = 'text' | 'secondary' | 'muted' | 'primary' | 'secondary' | 'accent' | 'success' | 'warning' | 'error';

interface TextProps extends Omit<React.HTMLAttributes<HTMLElement>, 'color'> {
  size?: TextSize;
  color?: TextColor;
  weight?: 'light' | 'normal' | 'medium' | 'semibold' | 'bold';
  lineHeight?: 'tight' | 'normal' | 'relaxed' | 'loose';
  truncate?: boolean;
  as?: 'p' | 'span' | 'div';
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  marginBottom?: string;
}

const TextStyled = styled.p<{
  size: TextSize;
  color: TextColor;
  weight: string;
  lineHeight: string;
  truncate: boolean;
}>`
  margin: 0;
  font-family: ${typography.fontFamily.primary};
  font-size: ${props => typography.fontSize[props.size]};
  font-weight: ${props => typography.fontWeight[props.weight as keyof typeof typography.fontWeight]};
  line-height: ${props => typography.lineHeight[props.lineHeight as keyof typeof typography.lineHeight]};

  ${props => {
    switch (props.color) {
      case 'primary':
        return `color: ${props.theme.colors.primary};`;
      case 'secondary':
        return `color: ${props.theme.colors.secondary};`;
      case 'muted':
        return `color: ${props.theme.colors.textMuted};`;
      case 'accent':
        return `color: ${props.theme.colors.accent};`;
      case 'success':
        return `color: ${props.theme.colors.success};`;
      case 'warning':
        return `color: ${props.theme.colors.warning};`;
      case 'error':
        return `color: ${props.theme.colors.error};`;
      case 'text':
      default:
        return `color: ${props.theme.colors.text};`;
    }
  }}

  ${props => props.truncate && css`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `}
`;

export const Text: React.FC<TextProps> = ({
  size = 'base',
  color = 'text',
  weight = 'normal',
  lineHeight = 'normal',
  truncate = false,
  as = 'p',
  children,
  className,
  style,
  marginBottom,
  ...rest
}) => {
  const combinedStyle: React.CSSProperties | undefined =
    style || marginBottom ? { ...style, ...(marginBottom ? { marginBottom } : {}) } : undefined;
  return (
    <TextStyled
      as={as}
      size={size}
      color={color}
      weight={weight}
      lineHeight={lineHeight}
      truncate={truncate}
      className={className}
      style={combinedStyle}
      {...rest}
    >
      {children}
    </TextStyled>
  );
};

/**
 * Label - Form label component
 */
interface LabelProps {
  required?: boolean;
  error?: boolean;
  size?: 'sm' | 'base' | 'lg';
  children: React.ReactNode;
  htmlFor?: string;
  className?: string;
}

const LabelStyled = styled.label<{
  required: boolean;
  error: boolean;
  size: 'sm' | 'base' | 'lg';
}>`
  display: block;
  font-family: ${typography.fontFamily.primary};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.error ? props.theme.colors.error : props.theme.colors.text};
  margin-bottom: ${spacing[2]};

  ${props => {
    const sizes = {
      sm: typography.fontSize.sm,
      base: typography.fontSize.base,
      lg: typography.fontSize.lg,
    };
    return `font-size: ${sizes[props.size]};`;
  }}

  ${props => props.required && css`
    &::after {
      content: ' *';
      color: ${props.theme.colors.error};
      margin-left: ${spacing[1]};
    }
  `}
`;

export const Label: React.FC<LabelProps> = ({
  required = false,
  error = false,
  size = 'base',
  children,
  htmlFor,
  className,
}) => {
  return (
    <LabelStyled
      required={required}
      error={error}
      size={size}
      htmlFor={htmlFor}
      className={className}
    >
      {children}
    </LabelStyled>
  );
};

/**
 * Code - Code text component
 */
export type CodeVariant = 'inline' | 'block';

interface CodeProps {
  variant?: CodeVariant;
  children: React.ReactNode;
  className?: string;
}

const CodeStyled = styled.code<{ variant: CodeVariant }>`
  font-family: ${typography.fontFamily.monospace};
  font-size: ${typography.fontSize.sm};
  background: ${props => props.theme.colors.surfaceHover};
  color: ${props => props.theme.colors.text};
  padding: ${props => props.variant === 'inline' ? `${spacing[1]} ${spacing[2]}` : spacing[4]};
  border-radius: ${props => props.variant === 'inline' ? '4px' : '8px'};
  border: 1px solid ${props => props.theme.colors.border};
  
  ${props => props.variant === 'block' && css`
    display: block;
    overflow-x: auto;
    line-height: ${typography.lineHeight.relaxed};
  `}
`;

export const Code: React.FC<CodeProps> = ({
  variant = 'inline',
  children,
  className,
}) => {
  return (
    <CodeStyled variant={variant} className={className}>
      {children}
    </CodeStyled>
  );
};
